import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  Card,
  CardContent,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LineChart, mangoFusionPalette } from '@mui/x-charts-pro';
import { GuestJourneyMemberTrendsResponse } from 'api/reports-service/guest-journey-reports.types';
import { compactNumberValueFormatter } from 'components/charts/inc-charts.utils';
import {
  ChartsHighlightProvider,
  useChartsHighlight,
} from 'components/charts/inc-charts.context';
import HighlightedLegend from 'components/charts/inc-line-chart/legend/highlighted-legend.component';
import HighlightedLine from 'components/charts/inc-line-chart/line/highlighted-line.component';
import HighlightedTooltip from 'components/charts/inc-line-chart/tooltip/highlighted-tooltip.component';
import { getFormattedDate } from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import { useSearchParam } from 'components/search/search.hooks';
import { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export type MemberCountTrendsProps = {
  data?: GuestJourneyMemberTrendsResponse;
  loading?: boolean;
  /**
   * Returns the localized card title. Per-tab so Sliders / Churn can supply
   * their own copy without forking the component.
   */
  getTitle?: (t: TFunction) => string;
  'data-testid'?: string;
};

/**
 * Chart-scoped date range presets, independent of the report toolbar filter.
 * Matches story 357743: seven fixed presets plus a custom range. Ranges are
 * trailing windows ending today so the default (last 90 days, set in
 * `guest-journey.hooks`) round-trips back to the "Last 90 days" label.
 */
type TrendsPresetId =
  | 'last7Days'
  | 'last30Days'
  | 'last60Days'
  | 'last90Days'
  | 'monthToDate'
  | 'yearToDate'
  | 'lastYear';

type TrendsRangeId = TrendsPresetId | 'custom';

const PRESET_IDS: TrendsPresetId[] = [
  'last7Days',
  'last30Days',
  'last60Days',
  'last90Days',
  'monthToDate',
  'yearToDate',
  'lastYear',
];

const presetRange = (
  id: TrendsPresetId,
  now: DateTime,
): [DateTime, DateTime] => {
  const end = now.endOf('day');
  switch (id) {
    case 'last7Days':
      return [now.minus({ days: 7 }).startOf('day'), end];
    case 'last30Days':
      return [now.minus({ days: 30 }).startOf('day'), end];
    case 'last60Days':
      return [now.minus({ days: 60 }).startOf('day'), end];
    case 'last90Days':
      return [now.minus({ days: 90 }).startOf('day'), end];
    case 'monthToDate':
      return [now.startOf('month'), end];
    case 'yearToDate':
      return [now.startOf('year'), end];
    case 'lastYear':
    default:
      return [
        now.minus({ years: 1 }).startOf('year'),
        now.minus({ years: 1 }).endOf('year'),
      ];
  }
};

/**
 * Resolve which preset (if any) the current start/end correspond to. Falls back
 * to `last90Days` when no range is set, and `custom` when the range matches no
 * preset (e.g. a user-picked window).
 */
const resolveSelectedId = (
  start: DateTime | null,
  end: DateTime | null,
  now: DateTime,
): TrendsRangeId => {
  if (!start) return 'last90Days';
  const startIso = start.toISODate();
  const endIso = end?.toISODate();
  const match = PRESET_IDS.find(id => {
    const [s, e] = presetRange(id, now);
    return s.toISODate() === startIso && e.toISODate() === endIso;
  });
  return match ?? 'custom';
};

/**
 * Static label lookup. Keys are referenced as full string literals (not built
 * dynamically) so the i18next-scanner pre-commit hook detects them and doesn't
 * prune them as unused.
 */
const PRESET_LABELS: Record<TrendsRangeId, (t: TFunction) => string> = {
  last7Days: t => t('page.guestJourney.trendsRange.last7Days'),
  last30Days: t => t('page.guestJourney.trendsRange.last30Days'),
  last60Days: t => t('page.guestJourney.trendsRange.last60Days'),
  last90Days: t => t('page.guestJourney.trendsRange.last90Days'),
  monthToDate: t => t('page.guestJourney.trendsRange.monthToDate'),
  yearToDate: t => t('page.guestJourney.trendsRange.yearToDate'),
  lastYear: t => t('page.guestJourney.trendsRange.lastYear'),
  custom: t => t('page.guestJourney.trendsRange.custom'),
};

const getPresetLabel = (id: TrendsRangeId, t: TFunction): string =>
  PRESET_LABELS[id](t);

const defaultGetTitle = (t: TFunction) =>
  t('page.guestJourney.activeCustomers.memberCountTrendsChart.title');

const MemberCountTrends = ({
  data,
  loading = false,
  getTitle = defaultGetTitle,
  'data-testid': dataTestId = 'ActiveCustomers.MemberCountTrends',
}: MemberCountTrendsProps) => {
  const { t } = useTranslation();
  const trendsStart = useSearchParam<DateTime>('trendsStart');
  const trendsEnd = useSearchParam<DateTime>('trendsEnd');
  // Shared highlight state: hovering a line (or legend item) thickens/brightens
  // that line and fades the rest, and bolds its legend label. The tooltip stays
  // MUI's default axis tooltip, so it still lists every series at the hovered x.
  const highlight = useChartsHighlight();

  const selectedId = resolveSelectedId(
    trendsStart?.value ?? null,
    trendsEnd?.value ?? null,
    DateTime.local(),
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [customMode, setCustomMode] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const closeMenu = () => {
    setAnchorEl(null);
    setCustomMode(false);
  };

  const applyPreset = (id: TrendsPresetId) => {
    const [start, end] = presetRange(id, DateTime.local());
    trendsStart?.setValue(start);
    trendsEnd?.setValue(end);
    closeMenu();
  };

  const applyCustom = () => {
    trendsStart?.setValue(DateTime.fromISO(customFrom).startOf('day'));
    trendsEnd?.setValue(DateTime.fromISO(customTo).endOf('day'));
    closeMenu();
  };

  // `selectedId === 'custom'` only resolves when a start is set (see
  // `resolveSelectedId`), so `getFormattedDate` always returns a non-empty
  // label here — no fallback needed.
  const buttonLabel =
    selectedId === 'custom'
      ? getFormattedDate([
          trendsStart?.value as DateTime,
          trendsEnd?.value as DateTime,
        ])
      : getPresetLabel(selectedId, t);

  // Hide the per-point circles for a clean line — except when there's a single
  // data point, which has no line to draw and would otherwise render as nothing.
  const singlePoint = (data?.xlabels?.length ?? 0) === 1;
  const series =
    data?.series?.map(s => ({
      label: s.label,
      data: s.data,
      showMark: singlePoint,
    })) ?? [];

  return (
    <Card data-testid={dataTestId}>
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { paddingBottom: 2 },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
          marginBottom={1}
        >
          <Typography variant="body2" component="div">
            {getTitle(t)}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            endIcon={<ExpandMoreIcon />}
            onClick={e => setAnchorEl(e.currentTarget)}
            data-testid={`${dataTestId}.RangeSelect`}
          >
            {buttonLabel}
          </Button>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {customMode ? (
              <Stack
                spacing={2}
                sx={{ p: 2, minWidth: 240 }}
                data-testid={`${dataTestId}.CustomRange`}
              >
                <TextField
                  type="date"
                  size="small"
                  label={t('page.guestJourney.trendsRange.from')}
                  InputLabelProps={{ shrink: true }}
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  inputProps={{
                    'data-testid': `${dataTestId}.CustomRange.From`,
                  }}
                />
                <TextField
                  type="date"
                  size="small"
                  label={t('page.guestJourney.trendsRange.to')}
                  InputLabelProps={{ shrink: true }}
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  inputProps={{ 'data-testid': `${dataTestId}.CustomRange.To` }}
                />
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button size="small" onClick={() => setCustomMode(false)}>
                    {t('page.guestJourney.trendsRange.cancel')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!customFrom || !customTo}
                    onClick={applyCustom}
                    data-testid={`${dataTestId}.CustomRange.Apply`}
                  >
                    {t('page.guestJourney.trendsRange.apply')}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <MenuList data-testid={`${dataTestId}.RangeMenu`}>
                {PRESET_IDS.map(id => (
                  <MenuItem
                    key={id}
                    selected={id === selectedId}
                    onClick={() => applyPreset(id)}
                    data-testid={`${dataTestId}.RangeOption.${id}`}
                  >
                    {getPresetLabel(id, t)}
                  </MenuItem>
                ))}
                <MenuItem
                  selected={selectedId === 'custom'}
                  onClick={() => setCustomMode(true)}
                  data-testid={`${dataTestId}.RangeOption.custom`}
                >
                  {getPresetLabel('custom', t)}
                </MenuItem>
              </MenuList>
            )}
          </Popover>
        </Stack>
        <Stack flexGrow={1} minHeight={400}>
          <ChartsHighlightProvider value={highlight}>
            <LineChart
              series={series}
              xAxis={[
                {
                  scaleType: 'point',
                  data: data?.xlabels ?? [],
                  // BE sends ISO dates (e.g. "2026-06-13"); abbreviate to "Jun
                  // 13". Fall back to the raw label for any non-date string.
                  valueFormatter: (value: string) => {
                    const date = DateTime.fromISO(value);
                    return date.isValid ? date.toFormat('LLL d') : value;
                  },
                },
              ]}
              yAxis={[{ valueFormatter: compactNumberValueFormatter() }]}
              colors={mangoFusionPalette}
              loading={loading}
              data-testid={`${dataTestId}.Chart`}
              // Controlled highlight, shared with the custom line + legend slots
              // via ChartsHighlightProvider. Tooltip left as the default (axis)
              // so it keeps listing every series' value at the hovered x.
              onHighlightChange={highlight.setHighlightedItem}
              highlightedItem={highlight.highlightedItem}
              slots={{
                line: HighlightedLine,
                legend: HighlightedLegend,
                tooltip: HighlightedTooltip,
              }}
              slotProps={{
                legend: {
                  position: { horizontal: 'center', vertical: 'bottom' },
                },
                // Axis trigger → the tooltip lists every series' value at the
                // hovered x; HighlightedTooltip bolds the hovered series' row.
                tooltip: { trigger: 'axis' },
              }}
              sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}
            />
          </ChartsHighlightProvider>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MemberCountTrends;
